import { useState } from 'react'
import { ArrowUpRight, ChevronDown } from 'lucide-react'
import { CopyButton, Highlight } from './StudyUi'

export type CriterionRef = {
  key: string
  prefix: 'INC' | 'EXC'
  number: string
  /** Full criterion text when it could be resolved, else the note's text. */
  text: string
  population?: string | null
  /** False when only a truncated note was available. */
  resolved: boolean
}

export type CriteriaDisplayMode = 'compact' | 'full'

export const isCriteriaDisplayMode = (value: unknown): value is CriteriaDisplayMode => value === 'compact' || value === 'full'

const CHIP_TONES = {
  INC: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50',
  EXC: 'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-900/50',
}

export function criterionLabel(ref: Pick<CriterionRef, 'prefix' | 'number'>) {
  return `${ref.prefix} #${ref.number}`
}

function preview(text: string, limit = 280) {
  return text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}…` : text
}

/**
 * Criteria referenced by a blueprint row. Compact mode shows "EXC #17" chips
 * that expand in place (inline, so nothing is clipped by scroll containers);
 * full mode shows every criterion's complete text.
 */
export function CriteriaReferences({ refs, mode, search = '', onOpenCriterion }: {
  refs: CriterionRef[]
  mode: CriteriaDisplayMode
  search?: string
  onOpenCriterion?: (key: string) => void
}) {
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  if (!refs.length) return null

  if (mode === 'full') {
    return (
      <ul className="space-y-1.5">
        {refs.map((ref) => (
          <li key={ref.key} className="text-sm leading-snug">
            <CriterionBadge refItem={ref} onOpen={onOpenCriterion} />{' '}
            <span className="whitespace-pre-line text-gray-700 dark:text-gray-200"><Highlight text={ref.text} query={search} /></span>
          </li>
        ))}
      </ul>
    )
  }

  const toggle = (key: string) => setOpen((current) => {
    const next = new Set(current)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })
  const expanded = refs.filter((ref) => open.has(ref.key))
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Linked eligibility criteria">
        {refs.map((ref) => {
          const isOpen = open.has(ref.key)
          return (
            <button
              key={ref.key}
              type="button"
              aria-expanded={isOpen}
              title={preview(ref.text)}
              onClick={() => toggle(ref.key)}
              className={`inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 text-xs font-semibold tabular-nums transition-colors ${CHIP_TONES[ref.prefix]} ${isOpen ? 'ring-1 ring-current' : ''}`}
            >
              {criterionLabel(ref)}
              <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden={true} />
            </button>
          )
        })}
      </div>
      {expanded.map((ref) => (
        <div key={ref.key} className="rounded-md border border-gray-200 bg-white p-2.5 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="text-xs font-semibold text-gray-900 dark:text-white">{criterionLabel(ref)}</span>
              {ref.population && <span className="truncate text-xs text-gray-500 dark:text-gray-400">{ref.population}</span>}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <CopyButton text={`${criterionLabel(ref)}: ${ref.text}`} iconOnly label="Copy criterion" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200" />
              {onOpenCriterion && (
                <button type="button" onClick={() => onOpenCriterion(ref.key)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200" title="Open in I/E tab" aria-label={`Open ${criterionLabel(ref)} in the I/E tab`}>
                  <ArrowUpRight className="h-4 w-4" aria-hidden={true} />
                </button>
              )}
            </div>
          </div>
          <p className="whitespace-pre-line leading-snug text-gray-700 dark:text-gray-200"><Highlight text={ref.text} query={search} /></p>
          {!ref.resolved && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Full text not found in the extracted criteria; showing the build note.</p>}
        </div>
      ))}
    </div>
  )
}

function CriterionBadge({ refItem, onOpen }: { refItem: CriterionRef; onOpen?: (key: string) => void }) {
  const className = `inline-flex rounded border px-1 text-xs font-semibold tabular-nums ${CHIP_TONES[refItem.prefix]}`
  if (!onOpen) return <span className={className}>{criterionLabel(refItem)}</span>
  return (
    <button type="button" className={className} onClick={() => onOpen(refItem.key)} title="Open in I/E tab">
      {criterionLabel(refItem)}
    </button>
  )
}
